import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaHome,
  FaSpinner,
  FaSearch,
  FaGlobe,
  FaTh,
  FaUsers,
  FaCopy
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import HomeAssignUsersModal from './HomeAssignUsersModal';
import CloneNameModal from '../common/CloneNameModal';
import { useProductBuilderMode } from '../../utils/productBuilderMode';
import '../dashboard/css/DashboardManager.css';

const isProtectedHome = (home) =>
  home?.is_standard === true || home?.is_global_default === true;

const HomeManager = () => {
  const navigate = useNavigate();
  const productBuilderMode = useProductBuilderMode();
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredHomes, setFilteredHomes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingHome, setEditingHome] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    is_global_default: false,
    is_active: true,
    sort_order: 0
  });
  const [formError, setFormError] = useState(null);
  const [assignHome, setAssignHome] = useState(null);
  const [cloningHomeId, setCloningHomeId] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneError, setCloneError] = useState(null);

  const canManageGlobalFlag = productBuilderMode === true;
  const canDeleteHome = (home) =>
    !isProtectedHome(home) || productBuilderMode === true;

  const labelToName = (label) => {
    if (!label || typeof label !== 'string') return '';
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || '';
  };

  const normalizeName = (name) => labelToName(name);

  const handleLabelChange = (value, isEdit = false) => {
    setFormData((prev) => ({
      ...prev,
      label: value,
      ...(isEdit ? {} : { name: labelToName(value) })
    }));
  };

  const isNameTaken = (name, excludeId = null) => {
    const normalized = normalizeName(name);
    if (!normalized) return false;
    return homes.some(
      (h) =>
        normalizeName(h.name) === normalized &&
        String(h.id) !== String(excludeId || '')
    );
  };

  const loadHomes = async () => {
    try {
      setLoading(true);
      setError(null);
      const api = new DataApi('homes');
      const res = await api.get('/');
      if (res?.data?.success) {
        setHomes(res.data.data || []);
      } else if (Array.isArray(res?.data)) {
        setHomes(res.data);
      } else {
        setHomes([]);
      }
    } catch (err) {
      console.error('Error loading homes:', err);
      setError('Failed to load home layouts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHomes();
  }, []);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredHomes(homes);
      return;
    }
    setFilteredHomes(
      homes.filter(
        (h) =>
          (h.name || '').toLowerCase().includes(term) ||
          (h.label || '').toLowerCase().includes(term) ||
          (h.description || '').toLowerCase().includes(term)
      )
    );
  }, [homes, searchTerm]);

  const closeModal = () => {
    setShowModal(false);
    setEditingHome(null);
    setFormError(null);
  };

  const openCreate = () => {
    setEditingHome(null);
    setFormError(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      is_global_default: false,
      is_active: true,
      sort_order: 0
    });
    setShowModal(true);
  };

  const openEdit = (home) => {
    setEditingHome(home);
    setFormError(null);
    setFormData({
      name: home.name || '',
      label: home.label || '',
      description: home.description || '',
      is_global_default: !!home.is_global_default,
      is_active: home.is_active !== false,
      sort_order: home.sort_order || 0
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!editingHome) {
      const name = normalizeName(formData.name || formData.label);
      if (!name) {
        setFormError('Home name is required. Enter a label or name.');
        return;
      }
      if (isNameTaken(name)) {
        setFormError(`A home layout named "${name}" already exists. Choose a different label or name.`);
        return;
      }
    }

    const payload = editingHome
      ? {
          label: formData.label,
          description: formData.description,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
          ...(canManageGlobalFlag
            ? { is_global_default: formData.is_global_default }
            : {})
        }
      : {
          ...formData,
          name: normalizeName(formData.name || formData.label),
          // Tenant admins create assigned layouts only; global is fixed.
          is_global_default: canManageGlobalFlag ? formData.is_global_default : false
        };

    try {
      setSaving(true);
      const api = new DataApi('homes');
      if (editingHome?.id) {
        await api.put(`/${editingHome.id}`, payload);
      } else {
        await api.post('/', payload);
      }
      closeModal();
      loadHomes();
    } catch (err) {
      console.error('Error saving home:', err);
      const msg = err?.response?.data?.message || 'Failed to save home.';
      if (showModal) setFormError(msg);
      else setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (home) => {
    if (!canDeleteHome(home)) {
      window.alert(
        'The global standard home layout cannot be deleted. Only Product Builder can remove it.'
      );
      return;
    }
    if (!window.confirm(`Delete home "${home.label || home.name}"?`)) return;
    try {
      const api = new DataApi('homes');
      await api.delete(`/${home.id}`);
      loadHomes();
    } catch (err) {
      console.error('Error deleting home:', err);
      window.alert(err?.response?.data?.message || 'Failed to delete home.');
    }
  };

  const openWidgets = (home) => {
    navigate(`/setup/homewidget/${home.id}`, {
      state: {
        homeLabel: home.label || home.name,
        isGlobal: !!home.is_global_default,
        isStandard: !!home.is_standard
      }
    });
  };

  const openCloneModal = (home) => {
    const sourceLabel = home.label || home.name || 'Home';
    setCloneError(null);
    setCloneSource({
      home,
      defaultLabel: `${sourceLabel} (Copy)`,
      defaultName: labelToName(`${sourceLabel} (Copy)`),
    });
  };

  const closeCloneModal = () => {
    if (cloningHomeId) return;
    setCloneSource(null);
    setCloneError(null);
  };

  const handleConfirmCloneHome = async ({ label, name }) => {
    const home = cloneSource?.home;
    if (!home?.id) return;

    const normalized = normalizeName(name || label);
    if (isNameTaken(normalized, home.id)) {
      setCloneError(`A home layout named "${normalized}" already exists.`);
      return;
    }

    try {
      setCloningHomeId(home.id);
      setCloneError(null);
      setError(null);
      const api = new DataApi('homes');
      const res = await api.post(`/${home.id}/clone`, { label, name });
      const cloned = res?.data?.data;
      const count = res?.data?.widgetCount ?? 0;
      setCloneSource(null);
      await loadHomes();
      if (
        cloned?.id &&
        window.confirm(
          `Created "${cloned.label || cloned.name}" with ${count} widget(s).\n\nOpen the new home to edit widgets now?`
        )
      ) {
        openWidgets(cloned);
      }
    } catch (err) {
      console.error('Error cloning home:', err);
      setCloneError(
        err?.response?.data?.message || 'Failed to clone home layout.'
      );
    } finally {
      setCloningHomeId(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-manager">
        <div className="dashboard-loading-container">
          <FaSpinner className="dashboard-spinner fa-spin" />
          <p>Loading home layouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-manager">
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-title">
            <FaHome className="dashboard-header-icon" />
            <h1>Home Layouts</h1>
            <span className="dashboard-count">({filteredHomes.length} homes)</span>
          </div>
          <button type="button" className="dashboard-create-btn" onClick={openCreate}>
            <FaPlus />
            New Home
          </button>
        </div>
      </div>

      <div className="dashboard-search-filter-section">
        <div className="dashboard-search-box">
          <FaSearch className="dashboard-search-icon" />
          <input
            type="text"
            className="dashboard-search-input"
            placeholder="Search homes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="dashboard-error-message">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="dashboard-dashboards-container">
        {filteredHomes.length === 0 ? (
          <div className="dashboard-empty-state">
            <FaHome className="dashboard-empty-icon" />
            <h3>No home layouts found</h3>
            <p>
              {searchTerm
                ? 'No homes match your search.'
                : 'Create a home layout or run the home migrations.'}
            </p>
            {!searchTerm && (
              <button type="button" className="dashboard-create-btn" onClick={openCreate}>
                <FaPlus />
                New Home
              </button>
            )}
          </div>
        ) : (
          <div className="dashboard-table-container">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Widgets</th>
                  <th>Users</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHomes.map((home) => (
                  <tr key={home.id} className="dashboard-table-row">
                    <td className="dashboard-table-label">{home.label || '—'}</td>
                    <td className="dashboard-table-name">{home.name || '—'}</td>
                    <td>
                      {home.is_global_default || home.is_standard ? (
                        <span className="dashboard-home-global-badge">
                          <FaGlobe /> Global (standard)
                        </span>
                      ) : (
                        <span className="dashboard-table-name">Assigned</span>
                      )}
                    </td>
                    <td className="dashboard-table-name">{home.widget_count ?? 0}</td>
                    <td className="dashboard-table-name">{home.assigned_user_count ?? 0}</td>
                    <td className="dashboard-table-description">
                      {home.description || '—'}
                    </td>
                    <td className="dashboard-table-actions">
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-users-btn"
                        onClick={() => setAssignHome(home)}
                        title="Assign users"
                      >
                        <FaUsers />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-clone-btn"
                        onClick={() => openCloneModal(home)}
                        title="Clone home layout and widgets"
                        disabled={cloningHomeId === home.id}
                      >
                        {cloningHomeId === home.id ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          <FaCopy />
                        )}
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-widgets-btn"
                        onClick={() => openWidgets(home)}
                        title="Edit widgets"
                      >
                        <FaTh />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-edit-btn"
                        onClick={() => openEdit(home)}
                        title="Edit home"
                      >
                        <FaEdit />
                      </button>
                      {canDeleteHome(home) && (
                        <button
                          type="button"
                          className="dashboard-action-btn dashboard-delete-btn"
                          onClick={() => handleDelete(home)}
                          title={
                            isProtectedHome(home)
                              ? 'Delete global home (Product Builder)'
                              : 'Delete home'
                          }
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <HomeAssignUsersModal
        show={!!assignHome}
        home={assignHome}
        onClose={() => setAssignHome(null)}
        onSaved={loadHomes}
      />

      <CloneNameModal
        show={!!cloneSource}
        entityType="home layout"
        hint="Widgets will be copied; user assignments are not copied."
        sourceLabel={cloneSource?.home?.label || cloneSource?.home?.name || ''}
        defaultLabel={cloneSource?.defaultLabel}
        defaultName={cloneSource?.defaultName}
        deriveNameFromLabel={labelToName}
        saving={!!cloningHomeId}
        error={cloneError}
        onClose={closeCloneModal}
        onConfirm={handleConfirmCloneHome}
      />

      {showModal && (
        <div className="dashboard-modal-overlay" onClick={closeModal}>
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>{editingHome ? 'Edit Home' : 'New Home'}</h2>
              <button type="button" className="dashboard-close-btn" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="dashboard-modal-form">
              {formError && (
                <div className="dashboard-form-error" role="alert">
                  {formError}
                </div>
              )}
              <div className="dashboard-form-group">
                <label htmlFor="home-label">Label *</label>
                <input
                  id="home-label"
                  type="text"
                  required
                  value={formData.label}
                  onChange={(e) =>
                    handleLabelChange(e.target.value, !!editingHome)
                  }
                  placeholder="e.g. Sales Home"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="home-name">Name *</label>
                <input
                  id="home-name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={
                    editingHome
                      ? 'Cannot be changed after creation'
                      : 'Auto-populated from label (e.g., sales_home)'
                  }
                  disabled={!!editingHome}
                  readOnly={!!editingHome}
                  className={editingHome ? 'dashboard-name-disabled' : ''}
                />
                {!editingHome && (
                  <p className="dashboard-form-hint text-muted small mt-1 mb-0">
                    Auto-generated from the label. You can edit it before saving; must be unique.
                  </p>
                )}
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="home-description">Description</label>
                <textarea
                  id="home-description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Shown to admins; describes who should use this layout"
                />
              </div>
              {canManageGlobalFlag ? (
                <div className="dashboard-form-checkbox-row">
                  <input
                    id="home-global-default"
                    type="checkbox"
                    checked={formData.is_global_default}
                    onChange={(e) =>
                      setFormData({ ...formData, is_global_default: e.target.checked })
                    }
                  />
                  <label htmlFor="home-global-default">
                    <strong>Global default</strong> — shown to users who do not have an assigned
                    home layout. Only Product Builder can change this.
                  </label>
                </div>
              ) : editingHome && isProtectedHome(editingHome) ? (
                <div className="dashboard-form-checkbox-row">
                  <input id="home-global-default" type="checkbox" checked disabled readOnly />
                  <label htmlFor="home-global-default">
                    <strong>Global standard home</strong> — this layout is the org default and
                    cannot be removed or reassigned except by Product Builder.
                  </label>
                </div>
              ) : null}
              <div className="dashboard-modal-actions">
                <button type="button" className="dashboard-cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="dashboard-save-btn" disabled={saving}>
                  {saving ? 'Saving…' : editingHome ? 'Update Home' : 'Create Home'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeManager;
